import { CameraFilledIcon, CheckmarkFilledIcon, EditIcon } from '@calizahq/react-components-v2'
import { formatInTimeZone } from 'date-fns-tz'
import React from 'react'

import { BottomSheet, Tag } from '@/components'
import { LineTagType } from '@/components/LineItem/LineItem'
import { UpdateIssueForm } from '@/forms/ReportIssue/UpdateIssueForm'
import { useToggle } from '@/hooks'
import { HomeIssueType, NewHomeIssue } from '@/types/HomeIssue'

import { HomeQualityCheckSummaryMedia } from '../Summary/Media'
import { ResolveHomeIssue } from './ResolveHomeIssue'

export const HomeIssueListColumns = (refetchOnSuccess: () => void) => {
  return [
    {
      Header: 'Location',
      accessor: 'homeLocation',
      width: 100,
    },
    {
      Header: 'Type',
      accessor: 'type',
      width: 125,
      Cell: ({ value }: { value: HomeIssueType }) => {
        return (
          <div className="flex w-full items-center justify-center">
            <Tag variant={getHomeIssueTypeClassName(value)}>{value}</Tag>
          </div>
        )
      },
    },
    {
      Header: 'Inventory Item',
      accessor: 'homeInventoryItem.name',
      width: 100,
      Cell: function _Cell({ value = '' }: { value: string }) {
        if (value) {
          return value
        } else {
          return 'N/A'
        }
      },
    },
    {
      Header: 'Date',
      accessor: 'createdAt',
      width: 120,
      Cell: function _Cell({ value = '' }: { value: string }) {
        return formattedDatetime(value)
      },
    },
    {
      Header: 'Notes',
      accessor: 'notes',
      minWidth: 300,
    },
    {
      Header: 'Actions',
      accessor: 'id',
      width: 60,
      Cell: function CellActions({ row }: { row: { original: NewHomeIssue } }) {
        const { isShown: modalShown, toggle: modalToggle } = useToggle()
        const { isShown: editHomeIssueModalShown, toggle: editHomeIssueModalToggle } = useToggle()
        const { isShown: mediaModalShown, toggle: mediaModalToggle } = useToggle()
        const { id } = row.original
        return (
          <>
            <button
              className={'mr-2 grid h-5 w-5 items-center justify-center'}
              onClick={modalToggle}
              title="Resolve Issue"
            >
              <CheckmarkFilledIcon className="text-green-300" width={24} />
            </button>
            <button
              className={'ml-2 mr-2 grid h-5 w-5 items-center justify-center'}
              onClick={editHomeIssueModalToggle}
              title="Edit Issue"
            >
              <EditIcon className="text-blue-300" width={24} />
            </button>
            {row.original.attachments && row.original.attachments.length > 0 ? (
              <button
                className={'ml-2 mr-2 grid h-5 w-5 items-center justify-center'}
                onClick={mediaModalToggle}
                title="View Media"
              >
                <CameraFilledIcon className="w-5 cursor-pointer icon-tertiary" />
              </button>
            ) : null}

            <BottomSheet isVisible={modalShown} mobileFullHeight={true} onClose={modalToggle}>
              <ResolveHomeIssue homeIssueId={id} refetchDetails={refetchOnSuccess} toggle={modalToggle} />
            </BottomSheet>

            <BottomSheet
              header="Edit Home Issue"
              isVisible={editHomeIssueModalShown}
              mobileFullHeight={true}
              onClose={editHomeIssueModalToggle}
            >
              <UpdateIssueForm
                homeIssue={row.original}
                refetchOnSuccess={refetchOnSuccess}
                toggle={editHomeIssueModalToggle}
              />
            </BottomSheet>

            {mediaModalShown && row.original.attachments && row.original.attachments.length > 0 ? (
              <BottomSheet header="Media" isVisible={mediaModalShown} onClose={mediaModalToggle}>
                <HomeQualityCheckSummaryMedia attachments={row.original.attachments} />
              </BottomSheet>
            ) : null}
          </>
        )
      },
    },
  ]
}

export const formattedDatetime = (value: string | undefined): string => {
  if (!value) return 'Invalid Date'
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
  try {
    const time = formatInTimeZone(value, timezone, 'MMM d yyyy, hh:mm a z')
    return time
  } catch (e) {
    return 'Invalid Date'
  }
}

export const getHomeIssueTypeClassName = (value: HomeIssueType | undefined): LineTagType => {
  switch (value) {
    case HomeIssueType.ACCESS:
      return 'success'
    case HomeIssueType.DAMAGE:
      return 'error'
    case HomeIssueType.REPLACEMENT_NEEDED:
      return 'warning'
    case HomeIssueType.WIFI:
      return 'info'
    default:
      return 'info'
  }
}
